import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangleIcon, ClockIcon, StarIcon, TrophyIcon } from 'lucide-react';
import { CHECKOUT_URL, PAYMENT_NOTE, PRICE, PRICE_FULL, PRICE_SHORT, Section } from './Shared';
import { useLaunchCountdown } from '../hooks/useLaunchCountdown';
import { trackInitiateCheckout } from '../utils/metaPixel';
function LaunchUrgency() {
  const {
    timeLeft,
    spotsTaken
  } = useLaunchCountdown();
  const format = (value: number) => value.toString().padStart(2, '0');
  const TimeBox = ({
    value,
    label
  }: {
    value: number;
    label: string;
  }) => <div className="flex flex-col items-center" data-id="element-135" data-mp="1mvepuciqxclk">
      <div className="min-w-[58px] rounded-xl bg-brand-purple px-3 py-2 font-mono text-2xl font-bold tabular-nums text-brand-goldLight shadow-sm ring-1 ring-brand-gold/40 md:min-w-[80px] md:px-5 md:py-3 md:text-4xl" data-id="element-136" data-mp="1mumu91p5feva">
        {format(value)}
      </div>
      <span className="mt-1.5 text-[10px] font-semibold uppercase tracking-wider text-brand-muted md:text-sm" data-id="element-137" data-mp="2kasnw6e9373u">
        {label}
      </span>
    </div>;
  return <div className="mx-auto mb-12 max-w-4xl" data-id="element-138" data-mp="12peuxm9c6fby">
      <div className="glass-panel rounded-3xl border-2 border-brand-gold/40 p-6 md:p-8" data-id="element-139" data-mp="yqp7t1yhxjf4">
        <div className="mb-5 flex items-center justify-center gap-2" data-id="element-140" data-mp="1chudakojp5xd">
          <ClockIcon className="h-5 w-5 text-brand-goldDark" data-id="element-141" data-mp="1cj5hgllwkfp0" />
          <h3 className="font-serif text-base font-bold text-brand-purpleDark md:text-xl" data-id="element-142" data-mp="17psv6i4p4kzf">
            ⏰ PRECIO DE LANZAMIENTO — CIERRA EN:
          </h3>
        </div>
        <div className="mb-5 flex items-center justify-center gap-2 md:gap-4" data-id="element-143" data-mp="1hgx7tb6wftr3">
          <TimeBox value={timeLeft.h} label="Horas" data-id="element-144" data-mp="1vg6pygeo0xxp" />
          <span className="pb-5 text-3xl font-bold text-brand-gold" data-id="element-145" data-mp="1vfn44olw9pee">:</span>
          <TimeBox value={timeLeft.m} label="Min" data-id="element-146" data-mp="1qi54jotromj2" />
          <span className="pb-5 text-3xl font-bold text-brand-gold" data-id="element-147" data-mp="1qf96m0hx5xum">:</span>
          <TimeBox value={timeLeft.s} label="Seg" data-id="element-148" data-mp="12e2tk3i5a78" />
        </div>
        <div className="mb-6 flex items-center justify-center gap-2 rounded-xl border border-brand-gold/50 bg-brand-goldLight/40 px-4 py-3" data-id="element-149" data-mp="3s98ioo2v8j8d">
          <AlertTriangleIcon className="h-5 w-5 shrink-0 text-brand-goldDark" data-id="element-150" data-mp="1w754njk8v1j8" />
          <p className="text-center text-sm font-semibold text-brand-text md:text-base" data-id="element-151" data-mp="214x551yyfvz1">
            Después, el precio de lanzamiento finaliza.
          </p>
        </div>
        <div className="rounded-2xl border border-brand-purple/20 bg-white p-4 md:p-5" data-id="element-152" data-mp="2a5bs05rkhj4n">
          <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between" data-id="element-153" data-mp="2f4odon6pck5n">
            <span className="flex items-center gap-2 text-sm font-semibold text-brand-text md:text-base" data-id="element-154" data-mp="2k2puf1gno7dy">
              <span className="relative flex h-2.5 w-2.5" data-id="element-155" data-mp="2otprc6prypgg">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-goldBright opacity-75" data-id="element-156" data-mp="2owc0ubw87f1b" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-brand-goldBright" data-id="element-157" data-mp="2xv63ok05h1nb" />
              </span>
              Cupos al precio de lanzamiento
            </span>
            <span className="text-xs font-bold uppercase tracking-wide text-brand-purple" data-id="element-158" data-mp="r6drra2os19i">Quedan pocos</span>
          </div>
          <div className="mb-2 h-2.5 w-full overflow-hidden rounded-full bg-brand-purple/10" data-id="element-159" data-mp="m7180y743ab5">
            <div className="h-full rounded-full bg-brand-purple" style={{
            width: `${spotsTaken}%`
          }} data-id="element-160" data-mp="i4xjp8zcgb8g" />
          </div>
          <p className="text-center text-xs text-brand-muted md:text-sm" data-id="element-161" data-mp="d8gvyfw23yvw">
            <strong className="text-brand-purple" data-id="element-162" data-mp="19vtxcbt8ajws">{spotsTaken}%</strong> de los cupos al precio de lanzamiento ya fueron
            tomados.
          </p>
        </div>
      </div>
    </div>;
}
function Pricing() {
  return <Section id="pricing" glow className="my-12 rounded-3xl border border-brand-gold/45 bg-brand-surfaceLight" data-id="element-163" data-mp="1130gi9y4vcgt">
      <div className="relative mb-10 text-center" data-id="element-164" data-mp="w58euo7osbzf">
        <span className="animate-glow-pulse mb-4 inline-flex items-center gap-2 rounded-full bg-brand-purple px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-brand-goldLight md:text-sm" data-id="element-165" data-mp="1tl4cnop2b2ud">
          ✨ Oferta de lanzamiento
        </span>
        <h2 className="font-display text-3xl font-bold text-brand-purpleDark md:text-4xl" data-id="element-166" data-mp="18gr9kc9nf6e3">
          TU <span className="headline-shine" data-id="element-167" data-mp="1df136ifq5yxl">MEGA PACK MAESTRO DE GEMOTERAPIA™</span>
        </h2>
        <p className="mt-3 text-sm text-brand-muted md:text-base" data-id="element-168" data-mp="zft1niw3aeq8">⏰ El precio de lanzamiento cierra pronto.</p>
      </div>

      <div className="mx-auto max-w-2xl space-y-5" data-id="element-169" data-mp="14ew7h5jw8oz9">
        <div className="relative rounded-3xl" data-id="element-170" data-mp="1s3xbmglp1dyq">
          <span className="glow-frame" aria-hidden="true" data-id="element-171" data-mp="1w47rqm379krb" />
          <div className="relative z-10 rounded-3xl bg-white p-5 shadow-lg md:p-7" data-id="element-172" data-mp="1w3pnp3aaq28u">
            <div className="mb-2 flex items-center gap-1.5 text-brand-goldDark" data-id="element-173" data-mp="1ibri32fsmohf">
              <TrophyIcon className="h-4 w-4 fill-current" data-id="element-174" data-mp="1nb479tu507n6" />
              <StarIcon className="h-4 w-4 fill-current" data-id="element-175" data-mp="1n9kr038hdwle" />
              <span className="text-[10px] font-bold uppercase tracking-wider md:text-sm" data-id="element-176" data-mp="2b0q62g6lkkun">El sistema más completo</span>
            </div>
            <div className="flex flex-wrap items-end justify-between gap-5" data-id="element-177" data-mp="2fu1f8nyvtdpq">
              <div className="max-w-md" data-id="element-178" data-mp="2p579bjp5lnhn">
                <h3 className="font-serif text-lg font-bold text-brand-purpleDark md:text-xl" data-id="element-179" data-mp="2l5omujhop4cp">
                  🏆 MEGA PACK MAESTRO DE GEMOTERAPIA™
                </h3>
                <p className="mt-1 text-sm text-brand-muted" data-id="element-180" data-mp="2yyxry7yln3pf">4 Tomos + 5 Bonus Estratégicos</p>
                <p className="mt-2 text-sm font-medium leading-relaxed text-brand-purple" data-id="element-181" data-mp="2tztlhp327k2s">
                  El sistema de consulta y organización para estudiar cristales, trabajar con protocolos, cuidar tu
                  energía, registrar sesiones y armonizar espacios con mayor claridad.
                </p>
              </div>
              <div className="shrink-0 text-right" data-id="element-182" data-mp="2b7imw7ffu8ah">
                <p className="text-sm text-brand-muted line-through" data-id="element-183" data-mp="2b5xsqix1crd5">{PRICE_FULL}</p>
                <p className="font-serif text-2xl font-bold text-brand-purple" data-id="element-184" data-mp="3rk7f57hpw61c">{PRICE}</p>
              </div>
            </div>
          </div>
        </div>

        <motion.a href={CHECKOUT_URL} onClick={trackInitiateCheckout} animate={{
        scale: [1, 1.015, 1]
      }} transition={{
        duration: 2.4,
        ease: 'easeInOut',
        repeat: Infinity
      }} whileHover={{
        scale: 1.03
      }} whileTap={{
        scale: 0.98
      }} className="cta-shimmer relative flex w-full justify-center overflow-hidden rounded-full bg-brand-purple px-6 py-5 text-center text-base font-bold text-brand-goldLight ring-1 ring-brand-gold/50 shadow-[0_12px_28px_rgba(42,18,89,.26)] md:text-xl" data-id="element-185" data-mp="3undm8kh2qr6v">
          <span className="relative z-10" data-id="element-186" data-mp="3fyqwdottmh1e">→ QUIERO MI MEGA PACK POR {PRICE_SHORT}</span>
        </motion.a>
        <p className="-mt-2 text-center text-xs font-medium text-brand-textLight md:text-sm" data-id="element-187" data-mp="3kvpm7v6gc1ts">{PAYMENT_NOTE}</p>
        <p className="text-center text-xs text-brand-muted md:text-sm" data-id="element-188" data-mp="3pq3p0ing1b8h">
          🛡️ Garantía de 7 días · 📥 Descarga inmediata · 🔒 Pago seguro
        </p>
      </div>
    </Section>;
}
export function LaunchOffer() {
  return <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-189" data-mp="2po6xniv8mdmk">
      <LaunchUrgency data-id="element-190" data-mp="2pnfe56ex06cn" />
      <Pricing data-id="element-191" data-mp="2pluj7fooule9" />
    </div>;
}